export const OVERLAY_POPUP_Z_INDEX_OFFSET = 2;
export const OVERLAY_POPUP_Z_INDEX_FALLBACK = 1302;
export const OVERLAY_TOOLTIP_Z_INDEX_FALLBACK = 1303;
export const PRE_ONBOARDING_CHAT_Z_INDEX = 10050;

const POPUP_VAR = "--ll-overlay-popup-z-index";
const TOOLTIP_VAR = "--ll-overlay-tooltip-z-index";
const TRANSIENT_POPUP_SELECTOR = [
  ".MuiAutocomplete-popper",
  ".MuiPopover-root",
  ".MuiPopper-root",
  ".MuiPickersPopper-root",
  ".MuiPickersLayout-root",
  ".MuiTooltip-popper",
].join(",");

const activeOverlayZIndexes = new Map();
const overlayListeners = new Set();

// The existing overlay owners also supply a read-only presence signal for quiet UI.
export const getHasOpenOverlay = () => activeOverlayZIndexes.size > 0;
export const subscribeOverlayLayers = (listener) => {
  overlayListeners.add(listener);
  return () => overlayListeners.delete(listener);
};
const publishOverlayPresence = () => overlayListeners.forEach((listener) => listener());

const getDocumentElement = () =>
  typeof document !== "undefined" ? document.documentElement : null;

const toFiniteZIndex = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const getOverlayPopupZIndex = (overlayZIndex) => {
  const zIndex = toFiniteZIndex(overlayZIndex);
  if (zIndex === null) return OVERLAY_POPUP_Z_INDEX_FALLBACK;
  return zIndex + OVERLAY_POPUP_Z_INDEX_OFFSET;
};

const writePopupVariables = () => {
  const documentElement = getDocumentElement();
  if (!documentElement) return;

  const highestOverlayZIndex = Math.max(
    0,
    ...Array.from(activeOverlayZIndexes.values()).map((value) =>
      toFiniteZIndex(value) || 0,
    ),
  );
  const popupZIndex = highestOverlayZIndex
    ? getOverlayPopupZIndex(highestOverlayZIndex)
    : OVERLAY_POPUP_Z_INDEX_FALLBACK;
  const tooltipZIndex = Math.max(
    OVERLAY_TOOLTIP_Z_INDEX_FALLBACK,
    popupZIndex + 1,
  );

  documentElement.style.setProperty(POPUP_VAR, String(popupZIndex));
  documentElement.style.setProperty(TOOLTIP_VAR, String(tooltipZIndex));
};

const dismissTransientPopupPortals = () => {
  if (typeof document === "undefined" || typeof KeyboardEvent === "undefined") {
    return;
  }
  if (!document.querySelector(TRANSIENT_POPUP_SELECTOR)) return;

  document.dispatchEvent(
    new KeyboardEvent("keydown", {
      bubbles: true,
      cancelable: true,
      key: "Escape",
    }),
  );
};

const freezeTransientPopupPortalsAtLayer = (overlayZIndex) => {
  if (typeof document === "undefined") return;
  const popupZIndex = getOverlayPopupZIndex(overlayZIndex);
  document.querySelectorAll(TRANSIENT_POPUP_SELECTOR).forEach((node) => {
    node.style.zIndex = String(popupZIndex);
    node.setAttribute("data-ll-overlay-popup-layer", String(popupZIndex));
  });
};

export const registerOverlayPopupLayer = (id, overlayZIndex) => {
  if (!id) return () => {};
  const currentHighestOverlayZIndex = Math.max(
    0,
    ...Array.from(activeOverlayZIndexes.values()).map((value) =>
      toFiniteZIndex(value) || 0,
    ),
  );
  const nextOverlayZIndex = toFiniteZIndex(overlayZIndex) || 0;
  if (currentHighestOverlayZIndex && nextOverlayZIndex > currentHighestOverlayZIndex) {
    freezeTransientPopupPortalsAtLayer(currentHighestOverlayZIndex);
    dismissTransientPopupPortals();
  }

  activeOverlayZIndexes.set(id, overlayZIndex);
  writePopupVariables();
  publishOverlayPresence();

  return () => {
    activeOverlayZIndexes.delete(id);
    writePopupVariables();
    publishOverlayPresence();
  };
};
